/**
 * The Labs & Monitoring tab for an HRT / TRT patient: the hormone labs (add a result, void one with a reason), the
 * blood pressure readings, the monitoring schedule with its due dates, and the provider confirmations the engine
 * reads (developer spec tab "App Inputs"), each with who set it and when.
 *
 * Every write goes to /hrt/patients/:id/... and the API evaluates again, so the plan tab's bands follow at once.
 * Labs and readings may be entered by any clinic role (STAFF prepares); a confirmation is a clinical decision and
 * only a clinician or an admin sets one (the API refuses STAFF with 403, and the controls say so).
 * Nothing is deleted: a wrong value is voided with a reason and stays on the list, struck through.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, fieldErrorsFrom } from '../lib/api';
import { useAuth } from '../lib/auth';
import { IconPlus } from './icons';
import { InfoHint } from './ui';
import { MonitoringList } from './HrtSections';
import { SaveNote, StepCard, TickBox, fieldClass } from './HrtUi';
import {
  CLEARABLE_NAME,
  HRT_PROGRAM_KEY,
  errorText,
  hrtKey,
  shortDay,
  todayIso,
  type HormoneLab,
  type HrtConfirmation,
  type HrtPatientView,
  type VitalReading,
} from '../lib/hrt';

function useHrtWrite<TBody>(view: HrtPatientView, path: (body: TBody) => { method: 'post' | 'put'; url: string; body: unknown }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TBody) => {
      const call = path(body);
      return call.method === 'put' ? api.put(call.url, call.body) : api.post(call.url, call.body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: hrtKey(view.patient.id) });
      void queryClient.invalidateQueries({ queryKey: HRT_PROGRAM_KEY });
    },
  });
}

const smallButton =
  'inline-flex h-[30px] items-center gap-1.5 rounded-md border border-hairline bg-white px-3 text-[12.5px] font-medium text-navy hover:bg-mint disabled:opacity-50';
const primaryButton =
  'inline-flex h-[34px] items-center rounded-md bg-forest px-4 text-[13.5px] font-semibold text-white hover:bg-forest-deep disabled:opacity-50';
const linkButton = 'text-[12.5px] text-ink-muted underline underline-offset-2 hover:text-navy';

export function HrtLabsTab({ view }: { view: HrtPatientView }) {
  return (
    <div className="space-y-2.5" data-testid="hrt-labs-tab">
      <HormoneLabsCard view={view} />
      <BloodPressureCard view={view} />
      <StepCard
        title="Monitoring schedule"
        subtitle={
          view.monitoring
            ? view.monitoring.startDate
              ? `${view.monitoring.pathwayName}, counted from the start on ${shortDay(view.monitoring.startDate)}.`
              : `${view.monitoring.pathwayName} (${view.monitoring.basis === 'selected' ? 'the pathway chosen' : 'the top pathway'}). The dates are set from the day therapy starts.`
            : 'The follow-up visits and labs appear once a pathway is ranked.'
        }
      >
        {view.monitoring && view.monitoring.rows.length > 0 ? (
          <MonitoringList rows={view.monitoring.rows} />
        ) : (
          <p className="rounded-lg border border-dashed border-hairline px-4 py-4 text-[13.5px] text-ink-muted">Nothing scheduled yet.</p>
        )}
      </StepCard>
      <ConfirmationsCard view={view} />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Hormone labs
 * ------------------------------------------------------------------ */

function HormoneLabsCard({ view }: { view: HrtPatientView }) {
  const [adding, setAdding] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const live = view.labs.filter((lab) => !lab.voided).length;
  return (
    <StepCard
      title="Hormone labs"
      subtitle={`${live} ${live === 1 ? 'result' : 'results'} on file. MCOS evaluates again after every change.`}
      action={
        !adding && (
          <button type="button" className={smallButton} onClick={() => { setAdding(true); setNote(null); }} data-testid="add-lab">
            <IconPlus size={15} />
            Add result
          </button>
        )
      }
      testId="hormone-labs"
    >
      {adding && (
        <LabForm
          view={view}
          onDone={(text) => {
            setAdding(false);
            setNote(text);
          }}
          onCancel={() => setAdding(false)}
        />
      )}
      {note && <SaveNote tone="ok">{note}</SaveNote>}
      {view.labs.length === 0 ? (
        <p className="mt-2 rounded-lg border border-dashed border-hairline px-4 py-5 text-center text-[13.5px] text-ink-muted">
          {view.membership.track === 'female_hrt'
            ? 'No hormone labs yet. The female testosterone pathways need a baseline total testosterone and SHBG.'
            : 'No hormone labs yet. Add the results from the lab report; tick early-morning and fasting on each total testosterone draw.'}
        </p>
      ) : (
        <>
          <div className="mt-1 hidden grid-cols-[minmax(0,1.2fr)_minmax(0,0.9fr)_90px_minmax(0,1.3fr)_minmax(0,1fr)_70px] gap-3 border-b border-hairline px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-muted md:grid">
            <span>Test</span>
            <span>Result</span>
            <span>Drawn</span>
            <span>Draw</span>
            <span>Entered</span>
            <span />
          </div>
          <ul className="divide-y divide-hairline">
            {view.labs.map((lab) => (
              <LabRow key={lab.id} view={view} lab={lab} />
            ))}
          </ul>
        </>
      )}
    </StepCard>
  );
}

function Flag({ children }: { children: ReactNode }) {
  return <span className="rounded-[4px] bg-paper-deep px-1.5 py-[2px] text-[11px] font-semibold leading-none text-ink-muted">{children}</span>;
}

function LabRow({ view, lab }: { view: HrtPatientView; lab: HormoneLab }) {
  const [voiding, setVoiding] = useState(false);
  const voided = Boolean(lab.voided);
  return (
    <li className={`px-2 py-2.5 ${voided ? 'bg-paper' : ''}`} data-testid={`lab-${lab.id}`}>
      <div className="grid grid-cols-2 items-center gap-x-3 gap-y-1 md:grid-cols-[minmax(0,1.2fr)_minmax(0,0.9fr)_90px_minmax(0,1.3fr)_minmax(0,1fr)_70px]">
        <span className={`text-[13.5px] ${voided ? 'text-ink-muted' : 'font-medium text-navy'}`}>{lab.testLabel}</span>
        <span className={`text-[13.5px] max-md:text-right ${voided ? 'text-ink-muted line-through' : 'font-semibold text-navy'}`}>
          {lab.unit === '%' ? `${lab.value}%` : `${lab.value} ${lab.unit}`}
        </span>
        <span className="text-[12.5px] text-ink-muted">{shortDay(lab.drawnAt)}</span>
        <span className="flex flex-wrap gap-1 max-md:justify-end">
          {lab.earlyMorning && <Flag>Early morning</Flag>}
          {lab.fasting && <Flag>Fasting</Flag>}
          {lab.flaggedLow && <Flag>Flagged low</Flag>}
          {!lab.earlyMorning && !lab.fasting && !lab.flaggedLow && <span className="text-[12px] text-ink-muted">Not recorded</span>}
        </span>
        <span className="text-[12px] leading-tight text-ink-muted">
          {lab.enteredBy?.name ?? (lab.source === 'lab' ? 'Lab import' : 'Clinic')}, {shortDay(lab.createdAt)}
        </span>
        <span className="text-right">
          {!voided && !voiding && (
            <button type="button" className={linkButton} onClick={() => setVoiding(true)} data-testid={`void-lab-${lab.id}`}>
              Void
            </button>
          )}
        </span>
      </div>
      {lab.voided && (
        <p className="mt-1 text-[12px] text-ink-muted">
          Voided {shortDay(lab.voided.at)}
          {lab.voided.by ? ` by ${lab.voided.by.name}` : ''}: {lab.voided.reason}
        </p>
      )}
      {voiding && <VoidForm view={view} url={`/hrt/patients/${view.patient.id}/labs/${lab.id}/void`} what={`${lab.testLabel} ${lab.unit === '%' ? `${lab.value}%` : `${lab.value} ${lab.unit}`}`} onClose={() => setVoiding(false)} />}
    </li>
  );
}

function LabForm({ view, onDone, onCancel }: { view: HrtPatientView; onDone: (text: string) => void; onCancel: () => void }) {
  const tests = view.options.tests;
  const [test, setTest] = useState(tests[0]?.test ?? 'total_t');
  const units = tests.find((t) => t.test === test)?.units ?? [];
  const [unit, setUnit] = useState(units[0] ?? '');
  const [value, setValue] = useState('');
  const [drawnAt, setDrawnAt] = useState(todayIso());
  const [earlyMorning, setEarlyMorning] = useState(false);
  const [fasting, setFasting] = useState(false);
  const [flaggedLow, setFlaggedLow] = useState(false);
  const save = useHrtWrite<Record<string, unknown>>(view, (body) => ({ method: 'post', url: `/hrt/patients/${view.patient.id}/labs`, body }));
  const fields = fieldErrorsFrom(save.error);
  const number = Number(value);
  const valueOk = value.trim() !== '' && Number.isFinite(number) && number >= 0 && (test !== 'hct' || number <= 100);
  const label = tests.find((t) => t.test === test)?.label ?? test;

  return (
    <form
      className="mb-2 rounded-lg border border-hairline bg-paper px-3 py-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!valueOk || save.isPending) return;
        save.mutate(
          {
            test,
            value: number,
            unit,
            drawnAt,
            // An unticked box is "not recorded", never "no": only a recorded early-morning fasting draw counts.
            earlyMorning: earlyMorning ? true : null,
            fasting: fasting ? true : null,
            flaggedLow: flaggedLow ? true : null,
          },
          { onSuccess: () => onDone(`${label} ${number} ${unit} added. MCOS evaluated again.`) },
        );
      }}
      data-testid="lab-form"
    >
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
        <label className="col-span-2 block text-[12px] font-semibold text-navy sm:col-span-1">
          Test
          <select
            value={test}
            onChange={(event) => {
              setTest(event.target.value);
              setUnit(tests.find((t) => t.test === event.target.value)?.units[0] ?? '');
            }}
            className={`${fieldClass} mt-1`}
            data-testid="lab-test"
          >
            {tests.map((t) => (
              <option key={t.test} value={t.test}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-[12px] font-semibold text-navy">
          Value
          <input inputMode="decimal" value={value} onChange={(event) => setValue(event.target.value.replace(/[^0-9.]/g, ''))} className={`${fieldClass} mt-1`} data-testid="lab-value" />
          {fields.value && <span className="mt-0.5 block text-[11.5px] font-normal text-alert">{fields.value}</span>}
        </label>
        <label className="block text-[12px] font-semibold text-navy">
          Unit
          <select value={unit} onChange={(event) => setUnit(event.target.value)} className={`${fieldClass} mt-1`} data-testid="lab-unit">
            {units.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </label>
        <label className="col-span-2 block text-[12px] font-semibold text-navy sm:col-span-1">
          Drawn on
          <input type="date" value={drawnAt} max={todayIso()} onChange={(event) => setDrawnAt(event.target.value)} className={`${fieldClass} mt-1`} data-testid="lab-date" />
          {fields.drawnAt && <span className="mt-0.5 block text-[11.5px] font-normal text-alert">{fields.drawnAt}</span>}
        </label>
      </div>
      <div className="mt-2.5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-navy">
        <CheckLine checked={earlyMorning} onChange={setEarlyMorning} label="Early-morning draw" testId="lab-early" />
        <CheckLine checked={fasting} onChange={setFasting} label="Fasting" testId="lab-fasting" />
        <CheckLine checked={flaggedLow} onChange={setFlaggedLow} label="Flagged low by the lab" testId="lab-flagged" />
        <InfoHint label="About the draw boxes">
          The men's testosterone gate counts only early-morning fasting total testosterone draws on separate days, the two most recent (developer spec,
          Lab Gates). Leave a box unticked when the report does not say.
        </InfoHint>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <button type="submit" disabled={!valueOk || save.isPending} className={primaryButton} data-testid="lab-save">
          {save.isPending ? 'Saving' : 'Add result'}
        </button>
        <button type="button" onClick={onCancel} className={linkButton}>
          Cancel
        </button>
      </div>
      {save.isError && !fields.value && !fields.drawnAt && <SaveNote tone="error">{errorText(save.error)}</SaveNote>}
    </form>
  );
}

function CheckLine({ checked, onChange, label, testId, disabled }: { checked: boolean; onChange: (next: boolean) => void; label: string; testId?: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="inline-flex items-center gap-2 text-left disabled:opacity-60"
      data-testid={testId}
    >
      <TickBox checked={checked} size={20} />
      <span>{label}</span>
    </button>
  );
}

/** Void with a reason (5 characters or more, as the API asks). */
function VoidForm({ view, url, what, onClose }: { view: HrtPatientView; url: string; what: string; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const save = useHrtWrite<{ reason: string }>(view, (body) => ({ method: 'post', url, body }));
  return (
    <form
      className="mt-2 flex flex-wrap items-start gap-2 rounded-lg border border-hairline bg-risk-yellow-soft px-3 py-2.5"
      onSubmit={(event) => {
        event.preventDefault();
        if (reason.trim().length >= 5) save.mutate({ reason: reason.trim() }, { onSuccess: onClose });
      }}
      data-testid="void-form"
    >
      <label className="min-w-0 flex-1 basis-[240px] text-[12px] font-semibold text-navy">
        Why void {what}?
        <input value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} placeholder="For example: entered on the wrong patient" className={`${fieldClass} mt-1`} data-testid="void-reason" />
      </label>
      <div className="flex items-center gap-2 pt-[19px]">
        <button type="submit" disabled={reason.trim().length < 5 || save.isPending} className={primaryButton} data-testid="void-save">
          {save.isPending ? 'Voiding' : 'Void'}
        </button>
        <button type="button" onClick={onClose} className={linkButton}>
          Cancel
        </button>
      </div>
      {save.isError && <div className="basis-full"><SaveNote tone="error">{errorText(save.error)}</SaveNote></div>}
    </form>
  );
}

/* ------------------------------------------------------------------ *
 * Blood pressure
 * ------------------------------------------------------------------ */

const SOURCE_WORD: Record<string, string> = { clinic: 'Clinic', patient: 'Patient', device: 'Device' };

function BloodPressureCard({ view }: { view: HrtPatientView }) {
  const [adding, setAdding] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  return (
    <StepCard
      title="Blood pressure"
      subtitle="Checked at every TRT follow-up and before systemic estrogen. The newest reading counts."
      action={
        !adding && (
          <button type="button" className={smallButton} onClick={() => { setAdding(true); setNote(null); }} data-testid="add-bp">
            <IconPlus size={15} />
            Add reading
          </button>
        )
      }
      testId="blood-pressure"
    >
      {adding && (
        <VitalForm
          view={view}
          onDone={(text) => {
            setAdding(false);
            setNote(text);
          }}
          onCancel={() => setAdding(false)}
        />
      )}
      {note && <SaveNote tone="ok">{note}</SaveNote>}
      {view.vitals.length === 0 ? (
        <p className="mt-2 rounded-lg border border-dashed border-hairline px-4 py-5 text-center text-[13.5px] text-ink-muted">No blood pressure reading yet.</p>
      ) : (
        <ul className="divide-y divide-hairline">
          {view.vitals.map((vital) => (
            <VitalRow key={vital.id} view={view} vital={vital} />
          ))}
        </ul>
      )}
    </StepCard>
  );
}

function VitalRow({ view, vital }: { view: HrtPatientView; vital: VitalReading }) {
  const [voiding, setVoiding] = useState(false);
  const voided = Boolean(vital.voided);
  return (
    <li className={`px-2 py-2.5 ${voided ? 'bg-paper' : ''}`} data-testid={`vital-${vital.id}`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className={`min-w-[84px] font-display text-[17px] ${voided ? 'text-ink-muted line-through' : 'text-navy'}`} style={{ fontWeight: 600 }}>
          {vital.systolic}/{vital.diastolic}
        </span>
        <span className="text-[12.5px] text-ink-muted">{shortDay(vital.measuredAt)}</span>
        <span className="rounded-[4px] bg-paper-deep px-1.5 py-[2px] text-[11px] font-semibold text-ink-muted">{SOURCE_WORD[vital.source] ?? vital.source}</span>
        <span className="text-[12px] text-ink-muted">
          {vital.enteredBy?.name ?? 'Clinic'}, {shortDay(vital.createdAt)}
        </span>
        <span className="ml-auto">
          {!voided && !voiding && (
            <button type="button" className={linkButton} onClick={() => setVoiding(true)} data-testid={`void-vital-${vital.id}`}>
              Void
            </button>
          )}
        </span>
      </div>
      {vital.voided && (
        <p className="mt-1 text-[12px] text-ink-muted">
          Voided {shortDay(vital.voided.at)}
          {vital.voided.by ? ` by ${vital.voided.by.name}` : ''}: {vital.voided.reason}
        </p>
      )}
      {voiding && (
        <VoidForm view={view} url={`/hrt/patients/${view.patient.id}/vitals/${vital.id}/void`} what={`the reading ${vital.systolic}/${vital.diastolic}`} onClose={() => setVoiding(false)} />
      )}
    </li>
  );
}

function VitalForm({ view, onDone, onCancel }: { view: HrtPatientView; onDone: (text: string) => void; onCancel: () => void }) {
  const [systolic, setSystolic] = useState('');
  const [diastolic, setDiastolic] = useState('');
  const [measuredAt, setMeasuredAt] = useState(todayIso());
  const [source, setSource] = useState<'clinic' | 'patient' | 'device'>('clinic');
  const save = useHrtWrite<Record<string, unknown>>(view, (body) => ({ method: 'post', url: `/hrt/patients/${view.patient.id}/vitals`, body }));
  const sys = Number(systolic);
  const dia = Number(diastolic);
  const inRange = Number.isInteger(sys) && Number.isInteger(dia) && sys >= 50 && sys <= 300 && dia >= 20 && dia <= 200;
  const ok = systolic !== '' && diastolic !== '' && inRange && dia < sys;
  return (
    <form
      className="mb-2 rounded-lg border border-hairline bg-paper px-3 py-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (ok) save.mutate({ systolic: sys, diastolic: dia, measuredAt, source }, { onSuccess: () => onDone(`Blood pressure ${sys}/${dia} added. MCOS evaluated again.`) });
      }}
      data-testid="bp-form"
    >
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
        <label className="block text-[12px] font-semibold text-navy">
          Systolic
          <input inputMode="numeric" value={systolic} onChange={(event) => setSystolic(event.target.value.replace(/\D/g, ''))} className={`${fieldClass} mt-1`} data-testid="bp-systolic" />
        </label>
        <label className="block text-[12px] font-semibold text-navy">
          Diastolic
          <input inputMode="numeric" value={diastolic} onChange={(event) => setDiastolic(event.target.value.replace(/\D/g, ''))} className={`${fieldClass} mt-1`} data-testid="bp-diastolic" />
        </label>
        <label className="block text-[12px] font-semibold text-navy">
          Measured on
          <input type="date" value={measuredAt} max={todayIso()} onChange={(event) => setMeasuredAt(event.target.value)} className={`${fieldClass} mt-1`} />
        </label>
        <label className="block text-[12px] font-semibold text-navy">
          Taken by
          <select value={source} onChange={(event) => setSource(event.target.value as typeof source)} className={`${fieldClass} mt-1`}>
            <option value="clinic">Clinic</option>
            <option value="patient">Patient</option>
            <option value="device">Device</option>
          </select>
        </label>
      </div>
      {systolic !== '' && diastolic !== '' && !ok && (
        <p className="mt-1.5 text-[12px] text-alert">Systolic is 50 to 300 and diastolic 20 to 200, and diastolic is lower than systolic.</p>
      )}
      <div className="mt-3 flex items-center gap-3">
        <button type="submit" disabled={!ok || save.isPending} className={primaryButton} data-testid="bp-save">
          {save.isPending ? 'Saving' : 'Add reading'}
        </button>
        <button type="button" onClick={onCancel} className={linkButton}>
          Cancel
        </button>
      </div>
      {save.isError && <SaveNote tone="error">{errorText(save.error)}</SaveNote>}
    </form>
  );
}

/* ------------------------------------------------------------------ *
 * Provider confirmations
 * ------------------------------------------------------------------ */

const GROUPS: Array<{ title: string; keys: (key: string) => boolean }> = [
  {
    title: 'Safety and eligibility',
    keys: (k) =>
      ['hsddAssessed', 'contraceptionConfirmed', 'specialistCleared', 'bleedingEvaluated', 'clotEvaluated', 'virilizationReviewed', 'fertilityOverrideReason', 'individualizedRouteNeed', 'pituitaryConcern'].includes(k),
  },
  { title: 'Therapy', keys: (k) => ['telehealthOnly', 'declinesInjections', 'therapyStartDate'].includes(k) },
  { title: 'Monthly check-in questions', keys: (k) => ['erythrocytosis', 'suspectHypoxia', 'ironStudiesHigh', 'ironReview'].includes(k) },
  { title: 'Earlier response to a pathway', keys: (k) => k.startsWith('prior:') },
];

function ConfirmationsCard({ view }: { view: HrtPatientView }) {
  const clinician = useAuth().can('ADMIN', 'CLINICIAN');
  return (
    <StepCard
      title="Provider confirmations"
      subtitle="What the engine cannot read from a form. Each one is stored with who set it and when; a change is a new entry, never an edit."
      testId="confirmations"
    >
      {!clinician && <p className="mb-2 rounded-lg bg-paper-deep px-3.5 py-2 text-[13px] text-ink-muted">A clinician or an admin sets these. You can see them.</p>}
      <div className="space-y-3">
        {GROUPS.map((group) => {
          const rows = view.confirmations.filter((c) => group.keys(c.key));
          if (rows.length === 0) return null;
          return (
            <div key={group.title}>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.07em] text-ink-muted">{group.title}</p>
              <ul className="divide-y divide-hairline rounded-lg border border-hairline">
                {rows.map((row) => (
                  <ConfirmationRow key={row.key} view={view} row={row} canSet={clinician} />
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </StepCard>
  );
}

function setLine(row: HrtConfirmation): string {
  if (!row.setAt) return 'Not set';
  return `${row.setBy?.name ?? 'A clinician'}, ${shortDay(row.setAt)}`;
}

function ConfirmationRow({ view, row, canSet }: { view: HrtPatientView; row: HrtConfirmation; canSet: boolean }) {
  const save = useHrtWrite<{ value: unknown; note: string | null }>(view, (body) => ({
    method: 'put',
    url: `/hrt/patients/${view.patient.id}/confirmations/${encodeURIComponent(row.key)}`,
    body,
  }));
  const [draft, setDraft] = useState<unknown>(undefined);
  const [note, setNote] = useState('');
  const editing = draft !== undefined;
  const close = () => {
    setDraft(undefined);
    setNote('');
  };
  const commit = (value: unknown) => save.mutate({ value, note: note.trim() ? note.trim() : null }, { onSuccess: close });

  const valueWord = (() => {
    if (row.kind === 'boolean') return row.value === true ? 'Yes' : row.value === false ? 'No' : 'Not set';
    if (row.kind === 'rules') return Array.isArray(row.value) && row.value.length > 0 ? row.value.map((rule) => CLEARABLE_NAME[rule] ?? rule).join('; ') : 'None';
    if (row.kind === 'date') return typeof row.value === 'string' ? shortDay(row.value) : 'Not set';
    if (row.kind === 'prior') return typeof row.value === 'string' ? row.value.charAt(0).toUpperCase() + row.value.slice(1) : 'Not recorded';
    return typeof row.value === 'string' && row.value ? row.value : 'Not set';
  })();

  return (
    <li className="px-3 py-2.5" data-testid={`confirmation-${row.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1.5">
        <div className="flex min-w-0 flex-1 items-start gap-2.5">
          {row.kind === 'boolean' && (
            <button
              type="button"
              role="checkbox"
              aria-checked={row.value === true}
              aria-label={row.label}
              disabled={!canSet || save.isPending}
              onClick={() => setDraft(!(row.value === true))}
              className="mt-[1px] disabled:cursor-not-allowed disabled:opacity-70"
              data-testid={`toggle-${row.key}`}
            >
              <TickBox checked={draft !== undefined ? draft === true : row.value === true} size={20} />
            </button>
          )}
          <div className="min-w-0">
            <p className="text-[13.5px] font-medium leading-snug text-navy">{row.label}</p>
            <p className="text-[12px] text-ink-muted">
              {row.setAt && (
                <>
                  <span className="font-semibold text-navy">{valueWord}</span>
                  {' · '}
                </>
              )}
              {setLine(row)}
              {row.unlocks.length > 0 && row.kind !== 'rules' && (
                <span>
                  {' · Lifts: '}
                  {(row.unlockNames.length ? row.unlockNames : row.unlocks).join('; ')}
                </span>
              )}
            </p>
            {row.stale && (
              <p className="mt-1 rounded-md bg-risk-yellow-soft px-2 py-1 text-[12px] leading-snug text-risk-yellow" data-testid={`stale-${row.key}`}>
                Recorded before the check-in of {shortDay(row.stale.reportedAt)} reported it again: it no longer lifts the lock. Record it again after
                this episode is evaluated.
              </p>
            )}
            {row.note && <p className="mt-0.5 text-[12px] italic text-ink-muted">{row.note}</p>}
          </div>
        </div>
        {canSet && row.kind !== 'boolean' && !editing && (
          <button
            type="button"
            className={smallButton}
            onClick={() => setDraft(row.kind === 'rules' ? (Array.isArray(row.value) ? row.value : []) : (row.value ?? ''))}
            data-testid={`edit-${row.key}`}
          >
            {row.setAt ? 'Change' : 'Set'}
          </button>
        )}
      </div>

      {editing && (
        <div className="mt-2 rounded-lg border border-hairline bg-paper px-3 py-2.5" data-testid={`confirm-form-${row.key}`}>
          {row.kind === 'boolean' && (
            <p className="text-[13px] text-navy">
              Record <b>{row.label.toLowerCase()}</b> as <b>{draft === true ? 'yes' : 'no'}</b>?
            </p>
          )}
          {row.kind === 'text' && (
            <label className="block text-[12px] font-semibold text-navy">
              {row.label}
              <textarea value={String(draft ?? '')} onChange={(event) => setDraft(event.target.value)} rows={2} maxLength={2000} className={`${fieldClass} mt-1`} />
            </label>
          )}
          {row.kind === 'date' && (
            <label className="block text-[12px] font-semibold text-navy">
              {row.label}
              <input type="date" value={String(draft ?? '')} max={todayIso()} onChange={(event) => setDraft(event.target.value)} className={`${fieldClass} mt-1 max-w-[200px]`} />
            </label>
          )}
          {row.kind === 'prior' && (
            <label className="block text-[12px] font-semibold text-navy">
              {row.label}
              <select value={String(draft ?? '')} onChange={(event) => setDraft(event.target.value)} className={`${fieldClass} mt-1 max-w-[220px]`}>
                <option value="">Not recorded</option>
                {(row.choices ?? []).map((choice) => (
                  <option key={choice} value={choice}>
                    {choice.charAt(0).toUpperCase() + choice.slice(1)}
                  </option>
                ))}
              </select>
            </label>
          )}
          {row.kind === 'rules' && (
            <fieldset>
              <legend className="text-[12px] font-semibold text-navy">Cleared by a specialist, per rule</legend>
              <div className="mt-1.5 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {(row.choices ?? []).map((rule) => {
                  const list = Array.isArray(draft) ? (draft as string[]) : [];
                  const on = list.includes(rule);
                  return (
                    <CheckLine
                      key={rule}
                      checked={on}
                      onChange={() => setDraft(on ? list.filter((r) => r !== rule) : [...list, rule])}
                      label={CLEARABLE_NAME[rule] ?? rule}
                      testId={`clear-${rule}`}
                    />
                  );
                })}
              </div>
            </fieldset>
          )}
          <label className="mt-2 block text-[12px] font-semibold text-navy">
            Note, optional
            <input value={note} onChange={(event) => setNote(event.target.value)} maxLength={2000} className={`${fieldClass} mt-1`} placeholder="What you checked, for the record" />
          </label>
          <div className="mt-2.5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={save.isPending || (row.kind === 'text' && typeof draft === 'string' && draft.trim().length > 0 && draft.trim().length < 5)}
              onClick={() => {
                if (row.kind === 'text' || row.kind === 'date' || row.kind === 'prior') {
                  const text = typeof draft === 'string' ? draft.trim() : '';
                  commit(text === '' ? null : text);
                } else commit(draft);
              }}
              className={primaryButton}
              data-testid={`confirm-save-${row.key}`}
            >
              {save.isPending ? 'Saving' : 'Save'}
            </button>
            <button type="button" onClick={close} className={linkButton}>
              Cancel
            </button>
            {row.kind === 'text' && <span className="text-[12px] text-ink-muted">5 characters or more. Leave it empty to clear it.</span>}
          </div>
          {save.isError && <SaveNote tone="error">{errorText(save.error)}</SaveNote>}
        </div>
      )}
    </li>
  );
}
